import { Component, OnInit, OnDestroy, inject, HostListener, ChangeDetectorRef, NgZone } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router, RouterModule } from '@angular/router';
import { StoreService } from '../services/store.service';
import { PhotoboothService } from '../services/photobooth.service';
import { Auth, onAuthStateChanged, Unsubscribe } from '@angular/fire/auth';
import { AccessService, PagePermission } from '../services/access.service';
import { firstValueFrom, combineLatest, Subscription } from 'rxjs';

import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { QuillModule } from 'ngx-quill';

import { FormsModule } from '@angular/forms';
import { DeviceControlModalComponent } from './device-control-modal/device-control-modal.component';
import { DailyRevenueModalComponent } from './daily-revenue-modal/daily-revenue-modal.component';
import { DeviceAppReleaseModalComponent } from './device-app-release-modal/device-app-release-modal.component';

import { AgentProfitService } from '../services/agent-profit.service';
import { BrandService } from '../services/brand.service';
import { STANDALONE_HOUSEKEEPING_MESSAGES } from '../utils/standalone-housekeeping.util';
import {
  countFleetPublishTargets, devicePublishErrorMessage, devicePublishResultMessage, fleetPublishConfirmMessage,
  fleetPublishResultMessage, STANDALONE_NOT_IN_FLEET_PUBLISH
} from '../utils/publish-messages.util';

export interface PhotoboothMachine {
  storeId: string;
  storeName: string;
  location: string;
  deviceId: string;
  deviceName: string;
  status: string;
  isTesting?: boolean;
  isCustomized?: boolean;
  overrideGlobalLayout?: boolean;
  publishedVersion?: string;
  isPublishing?: boolean;
  lastPinged?: Date | string | null;
  cameraConnected?: boolean;
  cardMachineConnected?: boolean;
  billAcceptorConnected?: boolean;
  deviceOnline?: boolean;
  isInMaintenance?: boolean;
  remainingPapers?: number | string;
  overallStatus?: 'online' | 'warning' | 'offline';
  useCash?: boolean;
  useCard?: boolean;
  appVersion?: string;
}

@Component({
  selector: 'app-photobooth-management',
  standalone: true,
  imports: [CommonModule, RouterModule, FormsModule, DeviceControlModalComponent, DailyRevenueModalComponent, DeviceAppReleaseModalComponent, QuillModule],
  templateUrl: './photobooth-management.component.html',
  styleUrls: ['./photobooth-management.component.css']
})
export class PhotoboothManagementComponent implements OnInit, OnDestroy {
  private storeService = inject(StoreService);
  private router = inject(Router);
  private photoboothService = inject(PhotoboothService);
  private auth = inject(Auth);
  private accessService = inject(AccessService);
  private agentProfitService = inject(AgentProfitService);
  private brandService = inject(BrandService);
  private sanitizer = inject(DomSanitizer);
  private cdr = inject(ChangeDetectorRef);
  private ngZone = inject(NgZone);

  photobooths: PhotoboothMachine[] = [];
  isLoading: boolean = true;
  isPublishing: boolean = false;

  userRole: string | null = null;
  permissions: PagePermission[] = [];
  private authUnsubscribe?: Unsubscribe;

  sortColumn: string = 'overallStatus';
  sortDirection: 'asc' | 'desc' = 'asc';

  publishedVersions: any[] = [];
  selectedVersion: string = ''; // empty means 'Draft'
  activePublishedVersion: string | null = null;
  private publishListenerUnsubscribe: (() => void) | null = null;
  private dataSubscription?: Subscription;
  private hasShownErrorForVersion: { [version: number]: boolean } = {};

  activeTooltipDeviceId: string | null = null;

  readonly standaloneBadgeTooltip = STANDALONE_NOT_IN_FLEET_PUBLISH;
  /** Machines the in-flight fleet publish reaches (KAN-520); null when none is pending. */
  private pendingFleetPublishCount: number | null = null;

  toggleTooltip(deviceId: string, event: Event) {
    event.stopPropagation();
    this.activeTooltipDeviceId = this.activeTooltipDeviceId === deviceId ? null : deviceId;
  }

  controlModalDeviceId: string = '';
  controlModalDeviceName: string = '';

  revenueModalDeviceId: string = '';
  revenueModalDeviceName: string = '';

  editingStoreDeviceId: string | null = null;
  allActiveStores: any[] = [];

  showTestMachines: boolean = false;
  allPhotobooths: PhotoboothMachine[] = [];

  // Filter Dropdown States (Stores & Devices)
  showStoreFilterDropdown: boolean = false;
  showDeviceFilterDropdown: boolean = false;

  allUniqueStores: string[] = [];
  selectedStores = new Set<string>();
  storeFilterSearchText: string = '';

  allUniqueDevices: string[] = [];
  selectedDevices = new Set<string>();
  deviceFilterSearchText: string = '';

  get isAllStoresSelected(): boolean {
    return this.selectedStores.size === this.allUniqueStores.length && this.allUniqueStores.length > 0;
  }

  get isAllDevicesSelected(): boolean {
    return this.selectedDevices.size === this.allUniqueDevices.length && this.allUniqueDevices.length > 0;
  }

  toggleStoreFilter(): void {
    this.showStoreFilterDropdown = !this.showStoreFilterDropdown;
    this.showDeviceFilterDropdown = false;
  }

  toggleDeviceFilter(): void {
    this.showDeviceFilterDropdown = !this.showDeviceFilterDropdown;
    this.showStoreFilterDropdown = false;
  }

  getFilteredStores(): string[] {
    if (!this.storeFilterSearchText) return this.allUniqueStores;
    const lower = this.storeFilterSearchText.toLowerCase();
    return this.allUniqueStores.filter(s => s.toLowerCase().includes(lower));
  }

  getFilteredDevices(): string[] {
    if (!this.deviceFilterSearchText) return this.allUniqueDevices;
    const lower = this.deviceFilterSearchText.toLowerCase();
    return this.allUniqueDevices.filter(d => d.toLowerCase().includes(lower));
  }

  toggleAllStores(): void {
    if (this.isAllStoresSelected) {
      this.selectedStores.clear();
    } else {
      this.allUniqueStores.forEach(s => this.selectedStores.add(s));
    }
    this.applyFilters();
  }

  toggleAllDevices(): void {
    if (this.isAllDevicesSelected) {
      this.selectedDevices.clear();
    } else {
      this.allUniqueDevices.forEach(d => this.selectedDevices.add(d));
    }
    this.applyFilters();
  }

  toggleStoreSelection(store: string): void {
    if (this.selectedStores.has(store)) {
      this.selectedStores.delete(store);
    } else {
      this.selectedStores.add(store);
    }
    this.applyFilters();
  }

  toggleDeviceSelection(device: string): void {
    if (this.selectedDevices.has(device)) {
      this.selectedDevices.delete(device);
    } else {
      this.selectedDevices.add(device);
    }
    this.applyFilters();
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event?: MouseEvent): void {
    if (event) {
      const target = event.target as HTMLElement;
      if (!target.closest('.filter-dropdown')) {
        this.showStoreFilterDropdown = false;
        this.showDeviceFilterDropdown = false;
      }
      if (!target.closest('.tooltip-wrapper')) {
        this.activeTooltipDeviceId = null;
      }
      if (!target.closest('.split-release-btn-group')) {
        this.openReleaseDropdownVersion = null;
      }
    } else {
      this.activeTooltipDeviceId = null;
      this.openReleaseDropdownVersion = null;
    }
  }

  ngOnInit(): void {
    this.authUnsubscribe = onAuthStateChanged(this.auth, async (user) => {
      if (user) {
        const profile = await firstValueFrom(this.accessService.getUserProfile(user.uid));
        this.userRole = profile?.role || null;
        this.accessService.getPagePermissions().subscribe(perms => {
          this.permissions = perms;
        });
      }
    });

    this.loadPhotobooths();
    this.loadPublishedVersions();
    this.loadActiveAppRelease();

    // Listen for background publishing status
    this.publishListenerUnsubscribe = this.photoboothService.listenToLatestPublishStatus((doc) => {
      this.ngZone.run(() => {
        if (doc) {
          if (doc.status === 'publishing') {
            this.isPublishing = true;
            this.cdr.detectChanges();
          } else if (doc.status === 'failed') {
            this.isPublishing = false;
            this.cdr.detectChanges();
            if (!this.hasShownErrorForVersion[doc.version]) {
              alert(`Publishing version ${doc.version} failed: ${doc.error || 'Unknown error'}`);
              this.hasShownErrorForVersion[doc.version] = true;
            }
          } else if (doc.status === 'completed') {
            if (this.isPublishing) {
              this.isPublishing = false;
              this.cdr.detectChanges();
              alert(this.completedFleetPublishText(doc, 'Configuration published successfully!'));
              this.loadPublishedVersions();
            }
          }
        }
      });
    });
  }

  ngOnDestroy(): void {
    if (this.publishListenerUnsubscribe) {
      this.publishListenerUnsubscribe();
    }
    if (this.authUnsubscribe) {
      this.authUnsubscribe();
    }
    if (this.dataSubscription) {
      this.dataSubscription.unsubscribe();
    }
  }

  async loadPublishedVersions() {
    try {
      this.publishedVersions = await this.photoboothService.getPublishedVersions();
      this.activePublishedVersion = await this.photoboothService.getActivePublishedVersion();
    } catch (e) {
      console.error('Failed to load published versions', e);
    }
  }

  loadPhotobooths(): void {
    this.isLoading = true;
    this.dataSubscription = combineLatest([
      this.storeService.getStores(),
      this.storeService.getAllDevicesLive(),
      this.brandService.getBrands()
    ]).subscribe({
      next: async ([stores, devices, brands]) => {
        try {
          const konoboxBrandIds = new Set(
            brands.filter(b => (b.name || '').toLowerCase().includes('konobox') || (b.id || '').toLowerCase().includes('konobox')).map(b => b.id)
          );

          const activeStores = stores.filter(store => !store.isDeleted && !konoboxBrandIds.has(store.brandId) && (store.brandId || '').toLowerCase() !== 'konobox');
          this.allActiveStores = [...activeStores].sort((a, b) => {
            const idA = a.storeId || '';
            const idB = b.storeId || '';
            return idA.localeCompare(idB);
          });

          const storeMap = new Map<string, any>();
          activeStores.forEach(s => {
            if (s.id) storeMap.set(s.id, s);
            if (s.storeId) storeMap.set(s.storeId, s);
          });

          const list: PhotoboothMachine[] = [];

          const pingIntervalMinutes = await this.photoboothService.getPingInterval();
          const pingIntervalMs = pingIntervalMinutes * 60 * 1000;
          const now = new Date().getTime();

          for (const dev of devices) {
            if (dev.deviceId === 'global' || dev.id === 'global') {
              continue; // Skip global config placeholder
            }

            const associatedStore = dev.storeId ? storeMap.get(dev.storeId) : null;
            const devBrandId = dev.brandId || associatedStore?.brandId;
            if (devBrandId && (konoboxBrandIds.has(devBrandId) || devBrandId.toLowerCase() === 'konobox')) {
              continue; // Exclude Konobox devices from Photobooth Management (AC-10)
            }

            const deviceId = dev.deviceId || dev.id || 'N/A';

            let lastPingedDate: Date | null = null;
            if (dev.lastSeen) {
              if (typeof dev.lastSeen.toDate === 'function') {
                lastPingedDate = dev.lastSeen.toDate();
              } else if (dev.lastSeen instanceof Date && !isNaN(dev.lastSeen.getTime())) {
                lastPingedDate = dev.lastSeen;
              } else if (typeof dev.lastSeen === 'object' && 'seconds' in dev.lastSeen) {
                lastPingedDate = new Date(dev.lastSeen.seconds * 1000 + (dev.lastSeen.nanoseconds || 0) / 1000000);
              } else if (typeof dev.lastSeen === 'string' || typeof dev.lastSeen === 'number') {
                const parsed = new Date(dev.lastSeen);
                if (!isNaN(parsed.getTime())) {
                  lastPingedDate = parsed;
                }
              }
            }
            let isOnline = false;

            // Enforce ping calculation: only online if recent ping exists within interval
            if (lastPingedDate) {
              const lastPingTime = lastPingedDate.getTime();
              isOnline = (now - lastPingTime) <= pingIntervalMs;
              if (isOnline && dev.deviceOnline === false) {
                isOnline = false;
              }
            } else {
              isOnline = false;
            }

            const useCard = dev.useCard !== false; // default true
            const useCash = dev.useCash !== false; // default true

            const isInMaintenance = dev.isInMaintenance === true;
            const isCameraConnected = isOnline && dev.cameraConnected === true;

            let overallStatus: 'online' | 'warning' | 'offline' = 'online';
            if (!isOnline) {
              overallStatus = 'offline';
            } else {
              const allPeripheralsWorking = !isInMaintenance &&
                                            dev.cameraConnected === true && 
                                            (useCard ? dev.cardMachineConnected === true : true) && 
                                            (useCash ? dev.billAcceptorConnected === true : true);
              if (!allPeripheralsWorking) {
                overallStatus = 'warning';
              }
            }

            list.push({
              storeId: dev.storeId || '',
              storeName: associatedStore ? (associatedStore.storeId || associatedStore.name || 'Unnamed Store') : 'Unassigned Store',
              location: associatedStore ? (associatedStore.location || 'Unknown Location') : 'N/A',
              deviceId: deviceId,
              deviceName: dev.deviceName || 'Photo Booth Machine',
              status: 'Configured',
              isTesting: dev.isTesting === true,
              isCustomized: false, // Will be updated
              overrideGlobalLayout: dev.overrideGlobalLayout === true,
              lastPinged: lastPingedDate,
              cameraConnected: dev.cameraConnected === true,
              cardMachineConnected: dev.cardMachineConnected === true,
              billAcceptorConnected: dev.billAcceptorConnected === true,
              deviceOnline: isOnline,
              isInMaintenance: isInMaintenance,
              remainingPapers: dev.RemainingPapers !== undefined ? dev.RemainingPapers : dev.remainingPapers,
              overallStatus: overallStatus,
              useCash: useCash,
              useCard: useCard,
              appVersion: dev.appVersion || dev.installedAppVersion || dev.currentAppVersion || dev.installedVersion || dev.version || undefined
            });
          }

          // Check customization status and published version
          await Promise.all(list.map(async (booth) => {
            try {
              const hasCustom = await this.photoboothService.checkIfDeviceCustomized(booth.deviceId);
              booth.isCustomized = hasCustom;

              const devDoc = await this.storeService.getDevice(booth.deviceId);
              if (devDoc) {
                if (devDoc.overrideGlobalLayout !== undefined) {
                  booth.overrideGlobalLayout = !!devDoc.overrideGlobalLayout;
                }
                if (devDoc.publishedVersion) {
                  booth.publishedVersion = devDoc.publishedVersion.toString();
                } else if (this.activePublishedVersion) {
                  const isPublished = await this.photoboothService.checkIfDevicePublishedInVersion(booth.deviceId, this.activePublishedVersion);
                  booth.publishedVersion = isPublished ? this.activePublishedVersion.replace('v', '') : 'Not Published';
                } else {
                  booth.publishedVersion = 'Not Published';
                }
              } else if (this.activePublishedVersion) {
                const isPublished = await this.photoboothService.checkIfDevicePublishedInVersion(booth.deviceId, this.activePublishedVersion);
                booth.publishedVersion = isPublished ? this.activePublishedVersion.replace('v', '') : 'Not Published';
              } else {
                booth.publishedVersion = 'Not Published';
              }
            } catch (e) {
              console.error(`Error checking status for ${booth.deviceId}`, e);
              booth.publishedVersion = 'Unknown';
            }
          }));

          // Initial sort: Gray (offline) -> Yellow (warning) -> Green (online), ordered by status and device id
          this.allPhotobooths = list;
          this.sortColumn = 'overallStatus';
          this.sortDirection = 'asc';
          this.populateUniqueFilterOptions();
          this.applyFilters();
        } catch (error) {
          console.error('Error loading photobooth devices:', error);
        } finally {
          this.isLoading = false;
        }
      },
      error: (err) => {
        console.error('Error fetching stores and devices:', err);
        this.isLoading = false;
      }
    });
  }

  private populateUniqueFilterOptions(): void {
    const validData = this.showTestMachines ? this.allPhotobooths.filter(b => b.isTesting) : this.allPhotobooths.filter(b => !b.isTesting);
    
    // Stores
    const stores = Array.from(new Set(validData.map(b => b.storeName || 'Unassigned Store').filter(s => !!s))).sort();
    this.allUniqueStores = stores;
    if (this.selectedStores.size === 0) {
      stores.forEach(s => this.selectedStores.add(s));
    } else {
      // Retain existing valid selections
      const currentValid = new Set(stores);
      this.selectedStores = new Set(Array.from(this.selectedStores).filter(s => currentValid.has(s)));
      if (this.selectedStores.size === 0) {
        stores.forEach(s => this.selectedStores.add(s));
      }
    }

    // Devices
    const devices = Array.from(new Set(validData.map(b => b.deviceId).filter(d => !!d))).sort();
    this.allUniqueDevices = devices;
    if (this.selectedDevices.size === 0) {
      devices.forEach(d => this.selectedDevices.add(d));
    } else {
      // Retain existing valid selections
      const currentValid = new Set(devices);
      this.selectedDevices = new Set(Array.from(this.selectedDevices).filter(d => currentValid.has(d)));
      if (this.selectedDevices.size === 0) {
        devices.forEach(d => this.selectedDevices.add(d));
      }
    }
  }

  editGlobalLayout(): void {
    const queryParams = this.selectedVersion ? { version: this.selectedVersion } : {};
    this.router.navigate(['/home/photobooth-management/config/global/global'], { queryParams });
  }

  editDeviceLayout(booth: PhotoboothMachine): void {
    const queryParams = this.selectedVersion ? { version: this.selectedVersion } : {};
    this.router.navigate(['/home/photobooth-management/config', booth.storeId, booth.deviceId], { queryParams });
  }

  async publish(): Promise<void> {
    const counts = countFleetPublishTargets(this.allPhotobooths);
    if (confirm(fleetPublishConfirmMessage(counts))) {
      this.pendingFleetPublishCount = counts.publishTo;
      this.isPublishing = true;
      this.cdr.detectChanges();
      try {
        const nextVersion = await this.photoboothService.publishConfig();
        // Specifically attach a listener to this published version doc to guarantee completion is caught
        const versionUnsub = this.photoboothService.listenToVersionPublishStatus(`v${nextVersion}`, (doc) => {
          this.ngZone.run(() => {
            if (doc?.status === 'completed') {
              versionUnsub();
              if (this.isPublishing) {
                this.isPublishing = false;
                this.cdr.detectChanges();
                alert(this.completedFleetPublishText(doc, `Version ${nextVersion} published successfully!`));
                this.loadPublishedVersions();
              }
            } else if (doc?.status === 'failed') {
              versionUnsub();
              this.isPublishing = false;
              this.cdr.detectChanges();
              alert(`Publishing version ${nextVersion} failed: ${doc?.error || 'Unknown error'}`);
            }
          });
        });
      } catch (error) {
        console.error('Publishing initiation failed', error);
        alert('Failed to initiate publishing. See console for details.');
        this.isPublishing = false;
        this.cdr.detectChanges();
      }
    }
  }

  /** KAN-520: result text built from the completed version doc (skipped standalone ids); falls back when this session did not start the publish. */
  private completedFleetPublishText(versionDoc: any, fallback: string): string {
    const n = this.pendingFleetPublishCount;
    this.pendingFleetPublishCount = null;
    return n === null ? fallback : fleetPublishResultMessage(n, versionDoc);
  }

  async deleteHistoricalVersion(): Promise<void> {
    if (!this.selectedVersion) return;
    if (confirm(`Are you sure you want to permanently delete published version ${this.selectedVersion}?`)) {
      this.isLoading = true;
      try {
        await this.photoboothService.deleteHistoricalVersion(this.selectedVersion);
        this.selectedVersion = ''; // revert to draft
        await this.loadPublishedVersions();
      } catch (error) {
        console.error('Error deleting version', error);
        alert('Failed to delete version.');
      } finally {
        this.isLoading = false;
      }
    }
  }

  editStore(booth: PhotoboothMachine): void {
    this.editingStoreDeviceId = booth.deviceId;
  }

  cancelEditStore(): void {
    this.editingStoreDeviceId = null;
  }

  async saveStore(booth: PhotoboothMachine): Promise<void> {
    if (!booth.storeId) {
      alert("Please select a store or cancel.");
      return;
    }

    this.isLoading = true;
    try {
      const storeObj = this.allActiveStores.find(s => s.storeId === booth.storeId);
      const storeDocId = storeObj ? storeObj.id : '';

      await this.storeService.updateDeviceStore(booth.deviceId, booth.storeId, storeDocId);
      this.editingStoreDeviceId = null;
      // Note: Firebase onSnapshot will automatically trigger loadPhotobooths and refresh the grid.
    } catch (e) {
      console.error('Failed to update device store', e);
      alert('Failed to update store assignment.');
    } finally {
      this.isLoading = false;
    }
  }

  chatAboutDevice(booth: PhotoboothMachine): void {
    if (!booth || !booth.deviceId) return;
    this.agentProfitService.openChatForDevice(booth.deviceId);
  }

  async revertToGlobal(booth: PhotoboothMachine): Promise<void> {
    if (confirm(`${STANDALONE_HOUSEKEEPING_MESSAGES.revertTitle}\n\n${STANDALONE_HOUSEKEEPING_MESSAGES.revertBody}`)) {
      try {
        await this.photoboothService.revertDeviceToGlobal(booth.deviceId);
        booth.isCustomized = false;
        booth.overrideGlobalLayout = false;
        alert(STANDALONE_HOUSEKEEPING_MESSAGES.revertSuccess);
      } catch (error) {
        console.error('Failed to revert device to global:', error);
        alert('Failed to revert device. Check console for details.');
      }
    }
  }

  async publishToDevice(booth: PhotoboothMachine): Promise<void> {
    if (confirm(`Are you sure you want to publish the current draft directly to ${booth.deviceName}?`)) {
      booth.isPublishing = true;
      this.isPublishing = true;
      try {
        const request = await this.photoboothService.publishDeviceConfig(booth.deviceId);
        // KAN-515: a standalone publish reports what the function wrote (missing artwork, no frames); others keep the generic text.
        alert(devicePublishResultMessage(request) ?? `Successfully published to ${booth.deviceName}!`);
        const devDoc = await this.storeService.getDevice(booth.deviceId);
        if (devDoc && devDoc.publishedVersion) {
          booth.publishedVersion = devDoc.publishedVersion.toString();
        } else if (this.activePublishedVersion) {
          booth.publishedVersion = this.activePublishedVersion.replace('v', '');
        }
      } catch (error) {
        console.error('Publishing device failed', error);
        alert(devicePublishErrorMessage((error as Error)?.message, booth.overrideGlobalLayout === true));
      } finally {
        booth.isPublishing = false;
        this.isPublishing = false;
      }
    }
  }

  async setPingInterval(): Promise<void> {
    try {
      const currentInterval = await this.photoboothService.getPingInterval();
      const input = prompt('Enter ping interval in minutes:', currentInterval.toString());
      if (input !== null) {
        const minutes = parseInt(input, 10);
        if (!isNaN(minutes) && minutes > 0) {
          await this.photoboothService.setPingInterval(minutes);
          alert(`Ping interval successfully set to ${minutes} minutes.`);
        } else {
          alert('Invalid input. Please enter a positive number.');
        }
      }
    } catch (e) {
      console.error('Failed to set ping interval:', e);
      alert('An error occurred while setting the ping interval.');
    }
  }

  async deleteMachine(booth: PhotoboothMachine): Promise<void> {
    if (confirm(`Are you sure you want to completely delete the machine "${booth.deviceName}"? This action will permanently remove all associated settings, configurations, and storage assets. This cannot be undone.`)) {
      // Second confirmation to be safe
      if (prompt(`Please type "${booth.deviceName}" to confirm deletion:`) === booth.deviceName) {
        this.isLoading = true;
        try {
          await this.photoboothService.deleteMachine(booth.deviceId);
          alert(`Successfully deleted ${booth.deviceName}.`);
          this.loadPhotobooths();
        } catch (error) {
          console.error('Failed to delete machine:', error);
          alert('Failed to delete the machine. Check console for details.');
          this.isLoading = false;
        }
      } else {
        alert('Machine name did not match. Deletion cancelled.');
      }
    }
  }

  applyFilters(): void {
    let list = this.showTestMachines
      ? this.allPhotobooths.filter(b => b.isTesting)
      : this.allPhotobooths.filter(b => !b.isTesting);

    // Filter by selected stores
    if (this.selectedStores.size > 0 && this.allUniqueStores.length > 0) {
      list = list.filter(b => this.selectedStores.has(b.storeName || 'Unassigned Store'));
    } else if (this.allUniqueStores.length > 0) {
      list = [];
    }

    // Filter by selected devices
    if (this.selectedDevices.size > 0 && this.allUniqueDevices.length > 0) {
      list = list.filter(b => this.selectedDevices.has(b.deviceId));
    } else if (this.allUniqueDevices.length > 0) {
      list = [];
    }

    this.photobooths = list;
    this.applySort();
  }

  toggleTestMachines(): void {
    this.showTestMachines = !this.showTestMachines;
    this.populateUniqueFilterOptions();
    this.applyFilters();
  }

  // --- Device Selection for Targeted Actions ---
  selectedDeviceIds: Set<string> = new Set<string>();

  get selectedDeviceCount(): number {
    return this.selectedDeviceIds.size;
  }

  get isAllVisibleDevicesSelected(): boolean {
    if (this.photobooths.length === 0) return false;
    return this.photobooths.every(b => this.selectedDeviceIds.has(b.deviceId));
  }

  toggleSelectAllDevices(event: Event): void {
    const input = event.target as HTMLInputElement;
    if (input.checked) {
      this.photobooths.forEach(b => this.selectedDeviceIds.add(b.deviceId));
    } else {
      this.photobooths.forEach(b => this.selectedDeviceIds.delete(b.deviceId));
    }
  }

  toggleDeviceCheck(deviceId: string, event: Event): void {
    const input = event.target as HTMLInputElement;
    if (input.checked) {
      this.selectedDeviceIds.add(deviceId);
    } else {
      this.selectedDeviceIds.delete(deviceId);
    }
  }

  clearDeviceSelection(): void {
    this.selectedDeviceIds.clear();
  }

  sortBy(column: string): void {
    if (this.sortColumn === column) {
      this.sortDirection = this.sortDirection === 'asc' ? 'desc' : 'asc';
    } else {
      this.sortColumn = column;
      this.sortDirection = 'asc';
    }
    this.applySort();
  }

  private applySort(): void {
    this.photobooths.sort((a: any, b: any) => {
      let valA = a[this.sortColumn];
      let valB = b[this.sortColumn];

      if (valA == null) valA = '';
      if (valB == null) valB = '';

      if (this.sortColumn === 'overallStatus') {
        const rank = (status: string) => status === 'offline' ? 0 : status === 'warning' ? 1 : 2;
        const rA = rank(valA || 'offline');
        const rB = rank(valB || 'offline');
        if (rA < rB) return this.sortDirection === 'asc' ? -1 : 1;
        if (rA > rB) return this.sortDirection === 'asc' ? 1 : -1;
        return (a.deviceId || '').localeCompare(b.deviceId || '');
      }

      if (this.sortColumn === 'appVersion') {
        const vA = (a.appVersion || '').toString().trim();
        const vB = (b.appVersion || '').toString().trim();
        if (!vA && !vB) return (a.deviceId || '').localeCompare(b.deviceId || '');
        if (!vA) return this.sortDirection === 'asc' ? 1 : -1;
        if (!vB) return this.sortDirection === 'asc' ? -1 : 1;
        const cmp = vA.localeCompare(vB, undefined, { numeric: true, sensitivity: 'base' });
        if (cmp !== 0) return this.sortDirection === 'asc' ? cmp : -cmp;
        return (a.deviceId || '').localeCompare(b.deviceId || '');
      }

      if (typeof valA === 'string' && typeof valB === 'string') {
        const compare = valA.localeCompare(valB);
        if (compare !== 0) {
          return this.sortDirection === 'asc' ? compare : -compare;
        }
      } else {
        if (valA < valB) return this.sortDirection === 'asc' ? -1 : 1;
        if (valA > valB) return this.sortDirection === 'asc' ? 1 : -1;
      }
      return (a.deviceId || '').localeCompare(b.deviceId || '');
    });
  }

  openControlModal(booth: PhotoboothMachine): void {
    this.controlModalDeviceId = booth.deviceId;
    this.controlModalDeviceName = booth.deviceName;
  }

  closeControlModal(): void {
    this.controlModalDeviceId = '';
    this.controlModalDeviceName = '';
  }

  openRevenueModal(booth: PhotoboothMachine): void {
    this.revenueModalDeviceId = booth.deviceId;
    this.revenueModalDeviceName = booth.deviceName;
  }

  closeRevenueModal(): void {
    this.revenueModalDeviceId = '';
    this.revenueModalDeviceName = '';
  }

  canRestartAll(): boolean {
    if (this.userRole?.toLowerCase().trim() === 'superadmin') return true;
    const perm = this.permissions.find(p => p.id === 'photobooth-management_restart-all');
    return perm && perm.roles ? perm.roles.some(r => r.toLowerCase().trim() === this.userRole?.toLowerCase().trim()) : false;
  }

  canReleaseAll(): boolean {
    if (this.userRole?.toLowerCase().trim() === 'superadmin') return true;
    const perm = this.permissions.find(p => p.id === 'photobooth-management_release-all');
    return perm && perm.roles ? perm.roles.some(r => r.toLowerCase().trim() === this.userRole?.toLowerCase().trim()) : false;
  }

  canReleaseDevice(): boolean {
    if (this.userRole?.toLowerCase().trim() === 'superadmin') return true;
    const perm = this.permissions.find(p => p.id === 'photobooth-management_release-device');
    return perm && perm.roles ? perm.roles.some(r => r.toLowerCase().trim() === this.userRole?.toLowerCase().trim()) : false;
  }

  async restartAllMachines(): Promise<void> {
    if (!confirm('Are you sure you want to restart ALL machines? The restarts will be queued gracefully and will wait for any active customer sessions to finish.')) return;
    this.isLoading = true;
    let successCount = 0;
    let failCount = 0;

    const promises = this.photobooths.map(async (booth) => {
      try {
        await this.photoboothService.sendRemoteCommand(booth.deviceId, 'RESTART_APP');
        successCount++;
      } catch (e) {
        failCount++;
      }
    });

    await Promise.all(promises);
    this.isLoading = false;
    alert(`Restart commands sent. Success: ${successCount}, Failed: ${failCount}`);
  }

  // --- Device App Release Modal ---
  releaseModalDeviceId: string = '';
  releaseModalDeviceName: string = '';
  releaseModalCurrentAppVersion?: string;

  openReleaseAppModal(booth: PhotoboothMachine): void {
    this.releaseModalDeviceId = booth.deviceId;
    this.releaseModalDeviceName = booth.deviceName;
    this.releaseModalCurrentAppVersion = booth.appVersion;
  }

  closeReleaseAppModal(): void {
    this.releaseModalDeviceId = '';
    this.releaseModalDeviceName = '';
    this.releaseModalCurrentAppVersion = undefined;
  }

  onDeviceAppReleased(): void {
    this.closeReleaseAppModal();
  }

  // --- App Version Management ---

  activeAppRelease: any = null;
  showAppVersionModal: boolean = false;
  appVersionsList: any[] = [];
  isLoadingVersions: boolean = false;
  isReleasingAll: boolean = false;

  async loadActiveAppRelease(): Promise<void> {
    try {
      this.activeAppRelease = await this.photoboothService.getActiveAppRelease();
    } catch (err) {
      console.error('Error loading active app release', err);
    }
  }

  async openAppVersionModal(): Promise<void> {
    this.showAppVersionModal = true;
    await this.loadAppVersions();
  }

  closeAppVersionModal(): void {
    this.showAppVersionModal = false;
  }

  openReleaseDropdownVersion: string | null = null;

  toggleReleaseDropdown(version: string, event: Event): void {
    event.stopPropagation();
    this.openReleaseDropdownVersion = this.openReleaseDropdownVersion === version ? null : version;
  }

  async loadAppVersions(): Promise<void> {
    this.isLoadingVersions = true;
    try {
      this.appVersionsList = await this.photoboothService.getAppReleases();
    } catch (err) {
      console.error('Error loading app releases', err);
      alert('Failed to load app versions.');
    } finally {
      this.isLoadingVersions = false;
    }
  }

  canReleaseApp(): boolean {
    return this.selectedDeviceCount > 0 ? (this.canReleaseDevice() || this.canReleaseAll()) : this.canReleaseAll();
  }

  async releaseApp(versionData: any, forceUpdate: boolean = false): Promise<void> {
    if (this.selectedDeviceCount > 0) {
      const selectedIds = Array.from(this.selectedDeviceIds);
      const devCount = (await this.photoboothService.getAppReleaseTargets(selectedIds)).eligibleIds.length;
      if (devCount === 0) {
        alert('None of the selected machines can receive a PhotoLavie release (deleted or Konobox machines are skipped).');
        return;
      }
      const confirmMessage = forceUpdate
        ? `Are you sure you want to FORCE release Version ${versionData.version} to the ${devCount} selected machine(s)? These machines will force re-download and reinstall upon restart.`
        : `Are you sure you want to release Version ${versionData.version} to the ${devCount} selected machine(s)? These machines will download and apply this update upon restart.`;

      if (!confirm(confirmMessage)) {
        return;
      }
      this.isReleasingAll = true;
      try {
        const { successCount, failCount, skippedCount } = await this.photoboothService.releaseAppToSelectedDevices(selectedIds, versionData, true, forceUpdate);
        alert(`${forceUpdate ? 'Force Release' : 'Release'} initiated for ${devCount} selected machine(s). Successfully targeted: ${successCount}, Failed: ${failCount}, Skipped: ${skippedCount}`);
        this.clearDeviceSelection();
        this.closeAppVersionModal();
        await this.loadPhotobooths();
      } catch (err: any) {
        console.error('Failed to release app to selected devices', err);
        alert(`Failed to release to selected devices: ${err.message || err}`);
      } finally {
        this.isReleasingAll = false;
      }
    } else {
      await this.releaseAppToAll(versionData, forceUpdate);
    }
  }

  async releaseAppToAll(versionData: any, forceUpdate: boolean = false): Promise<void> {
    const targetCount = (await this.photoboothService.getAppReleaseTargets()).eligibleIds.length;
    const confirmMessage = forceUpdate
      ? `Are you sure you want to FORCE release Version ${versionData.version} to ALL ${targetCount} active PhotoLavie machines? This will force every machine to re-download and re-install this version immediately upon restart.`
      : `Are you sure you want to release Version ${versionData.version} to ALL ${targetCount} active PhotoLavie machines? Each machine will download and apply this update upon restart.`;

    if (!confirm(confirmMessage)) {
      return;
    }
    this.isReleasingAll = true;
    try {
      const { successCount, failCount, skippedCount } = await this.photoboothService.releaseAppToAllDevices(versionData, true, forceUpdate);
      alert(`${forceUpdate ? 'Force Release' : 'Release'} initiated for all machines. Successfully targeted: ${successCount}, Failed: ${failCount}, Skipped (Konobox/deleted): ${skippedCount}`);
      await this.loadActiveAppRelease();
      await this.loadPhotobooths();
    } catch (err: any) {
      console.error('Failed to release app to all machines', err);
      alert(`Failed to release to all machines: ${err.message || err}`);
    } finally {
      this.isReleasingAll = false;
    }
  }

  async deleteAppVersion(versionData: any): Promise<void> {
    if (confirm(`Are you sure you want to completely delete Version ${versionData.version}? This will remove it from storage and history.`)) {
      try {
        await this.photoboothService.deleteAppRelease(versionData.version, versionData.downloadUrl);
        await this.loadAppVersions(); // Refresh grid
      } catch (err) {
        console.error('Failed to delete app version', err);
        alert('Failed to delete version.');
      }
    }
  }

  // --- Release Notes Drawer in Manage Kiosk App Versions Modal ---
  showReleaseNoteDrawer: boolean = false;
  selectedNoteVersion: any = null;
  isLoadingNote: boolean = false;
  isSavingNote: boolean = false;
  isEditingNote: boolean = false;
  releaseNoteContent: string = '';
  releaseNoteUpdatedAt?: string;
  releaseNoteUpdatedBy?: string;
  noteFeedbackMessage: string = '';
  noteErrorMessage: string = '';

  quillConfig = {
    toolbar: [
      ['bold', 'italic', 'underline', 'strike'],
      ['blockquote', 'code-block'],
      [{ 'header': 1 }, { 'header': 2 }, { 'header': 3 }],
      [{ 'list': 'ordered' }, { 'list': 'bullet' }],
      [{ 'indent': '-1' }, { 'indent': '+1' }],
      [{ 'color': [] }, { 'background': [] }],
      [{ 'align': [] }],
      ['clean'],
      ['link']
    ]
  };

  async openReleaseNotesForVersion(versionData: any): Promise<void> {
    this.selectedNoteVersion = versionData;
    this.showReleaseNoteDrawer = true;
    await this.loadReleaseNoteForSelectedVersion();
  }

  closeReleaseNotesDrawer(): void {
    this.showReleaseNoteDrawer = false;
    this.isEditingNote = false;
    this.noteFeedbackMessage = '';
    this.noteErrorMessage = '';
  }

  async loadReleaseNoteForSelectedVersion(): Promise<void> {
    if (!this.selectedNoteVersion?.version) return;

    this.isLoadingNote = true;
    this.isEditingNote = false;
    this.noteFeedbackMessage = '';
    this.noteErrorMessage = '';

    try {
      const verData = await this.photoboothService.getAppReleaseVersion(this.selectedNoteVersion.version);
      if (verData) {
        this.releaseNoteContent = verData.releaseNote || '';
        this.releaseNoteUpdatedAt = verData.releaseNoteUpdatedAt;
        this.releaseNoteUpdatedBy = verData.releaseNoteUpdatedBy;
      } else {
        this.releaseNoteContent = '';
        this.releaseNoteUpdatedAt = undefined;
        this.releaseNoteUpdatedBy = undefined;
      }
    } catch (err: any) {
      console.error('Error loading release note', err);
      this.noteErrorMessage = 'Failed to load release notes for this version.';
    } finally {
      this.isLoadingNote = false;
    }
  }

  startEditNote(): void {
    this.isEditingNote = true;
    this.noteFeedbackMessage = '';
    this.noteErrorMessage = '';
  }

  cancelEditNote(): void {
    this.isEditingNote = false;
    this.noteFeedbackMessage = '';
    this.noteErrorMessage = '';
    this.loadReleaseNoteForSelectedVersion();
  }

  async saveReleaseNote(): Promise<void> {
    if (!this.selectedNoteVersion?.version) return;

    this.isSavingNote = true;
    this.noteFeedbackMessage = '';
    this.noteErrorMessage = '';

    try {
      await this.photoboothService.saveAppReleaseNote(this.selectedNoteVersion.version, this.releaseNoteContent || '');
      this.releaseNoteUpdatedAt = new Date().toISOString();
      this.noteFeedbackMessage = 'Release note saved successfully!';
      this.isEditingNote = false;
    } catch (err: any) {
      console.error('Error saving release note', err);
      this.noteErrorMessage = `Failed to save release note: ${err.message || err}`;
    } finally {
      this.isSavingNote = false;
    }
  }

  async deleteReleaseNote(): Promise<void> {
    if (!this.selectedNoteVersion?.version) return;

    if (!confirm(`Are you sure you want to delete the release note for v${this.selectedNoteVersion.version}?`)) {
      return;
    }

    this.isLoadingNote = true;
    this.noteFeedbackMessage = '';
    this.noteErrorMessage = '';

    try {
      await this.photoboothService.deleteAppReleaseNote(this.selectedNoteVersion.version);
      this.releaseNoteContent = '';
      this.releaseNoteUpdatedAt = undefined;
      this.releaseNoteUpdatedBy = undefined;
      this.isEditingNote = false;
      this.noteFeedbackMessage = 'Release note deleted.';
    } catch (err: any) {
      console.error('Error deleting release note', err);
      this.noteErrorMessage = `Failed to delete release note: ${err.message || err}`;
    } finally {
      this.isLoadingNote = false;
    }
  }

  getSanitizedNote(): SafeHtml {
    return this.sanitizer.bypassSecurityTrustHtml(this.releaseNoteContent || '');
  }
}
